import React, { useState } from "react";
import {
  Play,
  Star,
  Crown,
} from "@/icons/lucide-compat";
import type { ScriptableTemplate } from "@openreel/core";
import { ToolcraftClickableCard as ClickableCard } from "@openreel/ui";
import { CARD_BASE } from "../surface";
import { TemplateThumbnail } from "./TemplateThumbnail";

interface TemplateCardProps {
  template: ScriptableTemplate;
  onClick: () => void;
}

/**
 * The description, minus any duration it opens with.
 *
 * Six of the nine starters begin "10-second animated intro…", and the card
 * already states the duration in its own column, so left alone the two would
 * say the same thing twice on one small card. The modal shows the description
 * whole, because nothing states the duration alongside it there.
 */
function blurbOf(description: string): string {
  const trimmed = description.replace(/^(vertical\s+)?\d+[-\s]second\s+/i, "");
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Orientation in words rather than as a ratio. "9:16" is shorthand for people
 * who already edit video; this gallery is the first screen a new user sees.
 */
function shapeOf(width: number, height: number): string {
  if (width === height) return "Square";
  return height > width ? "Tall" : "Wide";
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);


  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  };

  return (
    <ClickableCard
      label={template.name}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      padding={0}
      variant="muted"
      className={CARD_BASE}
    >
      {/* One frame size for every card. A portrait template used to get a
          9:16 media area, so the cards in a row were different heights and the
          grid came out ragged. The template's real shape now shows as a pane
          inside this frame instead. */}
      <div className="relative flex aspect-video items-center justify-center overflow-hidden">
        {template.thumbnailUrl ? (
          <img
            src={template.thumbnailUrl}
            alt={template.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <TemplateThumbnail template={template} />
        )}

        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent transition-opacity duration-200 ${isHovered ? "opacity-100" : "opacity-0"}`}
        />

        {template.previewVideoUrl && isHovered && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="grid h-11 w-11 place-items-center rounded-full bg-black/70 backdrop-blur-sm">
              <Play
                size={18}
                className="ml-0.5 text-white"
                fill="currentColor"
              />
            </div>
          </div>
        )}

        {template.featured && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-amber-500 text-black text-[12px] font-normal rounded-full flex items-center gap-1">
            <Star size={10} fill="currentColor" />
            Featured
          </div>
        )}

        {template.premium && (
          <div className="absolute top-2 right-2 px-2 py-0.5 bg-white text-black text-[12px] font-normal rounded-full flex items-center gap-1">
            <Crown size={10} />
          </div>
        )}
      </div>

      {/* Two columns, not three stacked rows. The left carries what
          distinguishes one template from another; the right carries the two
          facts worth comparing, in a fixed position so they stack into a
          readable strip down the grid rather than ending somewhere different
          on every card. That ragged right edge was most of why the old block
          looked restless. */}
      <div className="flex items-start gap-3 px-3.5 pb-3.5 pt-3 text-left">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-medium leading-snug text-white">
            {template.name}
          </p>
          {/* The names are category labels: Quote Card, Countdown Timer, Lower
              Third. The description is the only thing that says how one
              differs from the next, and the card used to throw it away in
              favour of tags that restated the name. */}
          {template.description ? (
            <p className="mt-1 truncate text-[13px] leading-snug text-[#8f8f8f]">
              {blurbOf(template.description)}
            </p>
          ) : null}
        </div>

        <div className="shrink-0 text-right">
          <p className="text-[13px] tabular-nums leading-snug text-[#a8a8a8]">
            {formatDuration(template.timeline.duration)}
          </p>
          <p className="mt-1 text-[13px] leading-snug text-[#8f8f8f]">
            {shapeOf(template.settings.width, template.settings.height)}
          </p>
        </div>
      </div>

    </ClickableCard>
  );
};

export default TemplateCard;
